import ErrorOutline from '@mui/icons-material/ErrorOutline';
import Inbox from '@mui/icons-material/InboxOutlined';
import WifiOff from '@mui/icons-material/WifiOff';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { AppButton } from '@/components/atoms';

export type StatusTone = 'empty' | 'error' | 'offline';

const icons = { empty: Inbox, error: ErrorOutline, offline: WifiOff } as const;

export type StatusStateProps = {
  /** Use 'h1' where this is the only heading on the page. */
  headingLevel?: 'h1' | 'h2';
  tone: StatusTone;
  title: string;
  description?: string;
  /** A link (`to`) or a callback (`onClick`), e.g. Retry. */
  action?: { label: string; to: string } | { label: string; onClick: () => void };
};

/** The shared empty / error / offline screen (§63). */
export function StatusState({
  tone,
  title,
  description,
  action,
  headingLevel = 'h2',
}: StatusStateProps) {
  const Icon = icons[tone];
  return (
    <Stack
      role={tone === 'empty' ? 'status' : 'alert'}
      alignItems="center"
      textAlign="center"
      spacing={3}
      sx={{ py: 16, px: 4, maxWidth: 520, mx: 'auto' }}
    >
      <Icon
        aria-hidden
        sx={{ fontSize: 48, color: tone === 'empty' ? 'text.secondary' : 'error.main' }}
      />
      <Typography variant="h4" component={headingLevel}>
        {title}
      </Typography>
      {description && <Typography color="text.secondary">{description}</Typography>}
      {action &&
        ('to' in action ? (
          <AppButton to={action.to}>{action.label}</AppButton>
        ) : (
          <AppButton onClick={action.onClick}>{action.label}</AppButton>
        ))}
    </Stack>
  );
}
